<script setup>
import { computed, ref, watch } from 'vue';
import { formatMoney, formatSignedPercent, signClass } from '../format';
import { t } from '../i18n';
import { LEADERS, leaders } from '../marketOverview';
import { PANEL } from '../ui';
import { readUiState, writeUiState } from '../uiState';

// «Лидеры дня» (docs/market-overview/SPEC.md): five IMOEX shares up or down, «Рост | Падение» in one card.
// A row opens the instrument screen.
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open']);

const MODES = ['up', 'down'];

// Remembered on the device, as the heatmap's mode.
const mode = ref(MODES.includes(readUiState('markets.leaders', 'up')) ? readUiState('markets.leaders', 'up') : 'up');
watch(mode, (value) => writeUiState('markets.leaders', value));

const rows = computed(() => leaders(props.items, mode.value).map((share) => {
  const shown = Number(share.change.toFixed(2));
  return {
    uid: share.uid,
    ticker: share.ticker ?? '',
    name: share.name ?? '',
    logo: share.logo_url,
    color: share.logo_color,
    price: share.price ? formatMoney(share.price, share.currency) : '—',
    change: formatSignedPercent(shown, 2),
    changeClass: signClass(shown),
  };
}));

// A logo that fails to load falls back to the first letter on the brand color.
const broken = ref(new Set());

function onLogoError(uid) {
  broken.value = new Set([...broken.value, uid]);
}

const segment = (selected) => (selected ? 'bg-slate-100 text-slate-950' : 'text-slate-300');
</script>

<template>
  <section
    :class="PANEL"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
      {{ t('market.leaders.title') }}
    </h2>

    <div
      class="mt-3 flex rounded-full bg-slate-800 p-0.5"
      role="radiogroup"
      :aria-label="t('market.leaders.modeAria')"
    >
      <button
        v-for="key in MODES"
        :key="key"
        type="button"
        role="radio"
        :aria-checked="mode === key"
        class="h-8 flex-1 rounded-full text-[0.875rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        :class="segment(mode === key)"
        @click="mode = key"
      >
        {{ t(`market.leaders.${key}`) }}
      </button>
    </div>

    <div
      v-if="loading && items.length === 0"
      class="mt-3 space-y-2"
    >
      <div
        v-for="n in LEADERS"
        :key="n"
        class="h-11 rounded bg-slate-800 motion-safe:animate-pulse"
      />
    </div>
    <p
      v-else-if="rows.length === 0"
      class="mt-4 text-[0.9375rem] text-slate-400"
    >
      {{ t(mode === 'up' ? 'market.leaders.noneUp' : 'market.leaders.noneDown') }}
    </p>
    <ul
      v-else
      class="mt-2 divide-y divide-slate-800"
    >
      <li
        v-for="row in rows"
        :key="row.uid"
      >
        <button
          type="button"
          class="-mx-2 flex min-h-12 w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-slate-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
          @click="emit('open', row.uid)"
        >
          <img
            v-if="row.logo && !broken.has(row.uid)"
            :src="row.logo"
            alt=""
            class="size-8 shrink-0 rounded-full bg-slate-800 object-cover"
            loading="lazy"
            @error="onLogoError(row.uid)"
          >
          <span
            v-else
            class="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[0.6875rem] font-semibold text-white"
            :style="row.color ? { backgroundColor: row.color } : null"
            aria-hidden="true"
          >{{ (row.name || row.ticker).slice(0, 1).toUpperCase() }}</span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ row.ticker }}</span>
            <span class="mt-0.5 block truncate text-[0.8125rem] leading-4 text-slate-500">{{ row.name }}</span>
          </span>
          <span class="shrink-0 text-right tabular-nums">
            <span class="block text-[0.9375rem] leading-5 text-slate-100">{{ row.price }}</span>
            <span
              class="mt-0.5 block text-[0.8125rem] leading-4"
              :class="row.changeClass"
            >{{ row.change }}</span>
          </span>
        </button>
      </li>
    </ul>
  </section>
</template>
